import React, { useEffect, useRef, useState } from "react";
import { NavLink, Route, Switch, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import About from "../../pages/About/About";
import Blog from "../../pages/Blog/Blog";
import Contact from "../../pages/Contact/Contact";
import Portfolio from "../../pages/Portfolio/Portfolio";
import Service from "../../pages/Service/Service";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const listRef = useRef(null);
  const { pathname } = useLocation();
  const [edges, setEdges] = useState({ left: false, right: false });

  // The link row scrolls horizontally on narrow screens, so the active link can start
  // off-screen on a direct hit to /works or /contact. Centre it without scrolling the page.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector('[aria-current="page"]');
    if (!active || list.scrollWidth <= list.clientWidth) return;
    const listRect = list.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    list.scrollLeft +=
      activeRect.left - listRect.left - (listRect.width - activeRect.width) / 2;
  }, [pathname, i18n.language]);

  // Show an edge fade only on the side that actually has more content, so it never
  // dims a link (or its underline) that is already fully scrolled into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () =>
      setEdges({
        left: list.scrollLeft > 1,
        right: list.scrollWidth - list.clientWidth - list.scrollLeft > 1,
      });
    update();
    list.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(list);
    return () => {
      list.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [pathname, i18n.language]);

  const navbarData = [
    { id: 0, title: t("nav.about"), to: "/" },
    { id: 1, title: t("nav.services"), to: "/services" },
    { id: 2, title: t("nav.works"), to: "/works" },
    { id: 3, title: t("nav.blogs"), to: "/blogs" },
    { id: 4, title: t("nav.contact"), to: "/contact" },
  ];

  return (
    <>
      <nav className="sticky top-0 z-20 bg-surface/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          {/* min-w-0 + overflow-x-auto: the link row scrolls on narrow screens instead of
              forcing the whole document wider than the viewport. The edge fade is painted
              as a sibling overlay, not a mask, so it never dims the active-link underline. */}
          <div className="relative min-w-0 flex-1">
            <ul
              ref={listRef}
              className="nav-scroller flex flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain -mx-1 px-1"
            >
              {navbarData.map((el) => (
                <LinkItem el={el} key={el.id} />
              ))}
            </ul>
            {edges.left && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-surface to-transparent"
              />
            )}
            {edges.right && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-surface to-transparent"
              />
            )}
          </div>
          <LanguageSwitcher />
        </div>
      </nav>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-10">
        <Switch>
          <Route exact path="/">
            <About />
          </Route>
          <Route path="/services">
            <Service />
          </Route>
          <Route path="/works">
            <Portfolio />
          </Route>
          <Route path="/blogs">
            <Blog />
          </Route>
          <Route path="/contact">
            <Contact />
          </Route>
        </Switch>
      </div>
    </>
  );
};

export default Navbar;

const LinkItem = (props) => {
  const { title, to } = props.el;
  return (
    <li className="shrink-0">
      <NavLink
        to={to}
        exact={to === "/"}
        className="relative inline-flex items-center h-11 px-3 font-mono text-xs uppercase tracking-wide whitespace-nowrap text-muted hover:text-text transition"
        activeClassName="text-text after:absolute after:content-[''] after:left-3 after:right-3 after:bottom-0 after:h-0.5 after:bg-amber"
      >
        {title}
      </NavLink>
    </li>
  );
};
